import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { showErrorToast } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import {} from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { usePermissions, useUserPermissions } from '@CustomHooks';
import { AppDispatch, tableServerRevision } from '@Store';
import Modal from '../ui/modal';
import ModalLoading from '../ui/modal-loading';
import ModalSuccess from '../ui/modal-success';
import MultiSelect from '../ui/multiselect';
import Spinner from '../ui/spinner';

interface UserEditPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchUsers: () => void;
  IncrementEvent: (event: string) => void;
  email: string;
}

const UserEditPermissionsModal = ({
  isOpen,
  onClose,
  fetchUsers,
  IncrementEvent,
  email,
}: UserEditPermissionsModalProps) => {
  const { t } = useTranslation();

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [openSuccess, setOpenSuccess] = useState(false);

  const {
    data: availablePermissions,
    error: errorAvailablePermissions,
    isLoading: availablePermissionsLoading,
  } = usePermissions();

  const { query: userPermissionsQuery, updatePermissions } = useUserPermissions(isOpen ? email : null);

  const {
    data: userPermissions,
    error: errorUserPermissions,
    isLoading: userPermissionsLoading,
  } = userPermissionsQuery;

  // Sync selected permissions with server state when modal opens or userPermissions changes
  useEffect(() => {
    if (isOpen && userPermissions) {
      setSelectedPermissions(userPermissions);
    }
  }, [isOpen, userPermissions]);

  // Reset selected permissions when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPermissions([]);
    }
  }, [isOpen]);

  const handleClose = () => {
    setSelectedPermissions(userPermissions || []);
    onClose();
  };

  const onSubmit = () => {
    updatePermissions.mutate(
      { email, permissions: selectedPermissions },
      {
        onSuccess: () => {
          onClose();
          setOpenSuccess(true);
          setTimeout(() => {
            IncrementEvent('usersEvent');
            fetchUsers();
          }, 3000);
        },
        onError: (error) => {
          sendErrorEvent(error);
          setSelectedPermissions(userPermissions || []);
          showErrorToast(t('User.EditPermissionsError'));
        },
      },
    );
  };

  const isLoading = availablePermissionsLoading || userPermissionsLoading;
  const hasError = errorAvailablePermissions || errorUserPermissions;

  return (
    <>
      <Modal modalOpen={isOpen} closeModal={handleClose} title={t('User.EditPermissions')} closeClickOutside={false}>
        <div className="w-full">
          <div className="text-sm">
            {isLoading ? (
              <div className="py-4 flex justify-center">
                <Spinner />
              </div>
            ) : hasError ? (
              <p className="text-sm text-red-500">{t('User.EditPermissionsLoadingError')}</p>
            ) : availablePermissions && availablePermissions.length > 0 ? (
              <MultiSelect
                placeholder={t('User.EditPermissionsPlaceholder')}
                emptyText={t('User.EditPermissionsEmpty')}
                options={availablePermissions
                  .filter((perm) => !selectedPermissions.includes(perm.value))
                  .map((perm) => ({ label: perm.value, value: perm.value, description: perm.description }))}
                selected={selectedPermissions.map((perm) => ({ label: perm, value: perm }))}
                onChange={(selected) => {
                  setSelectedPermissions(selected.map((option) => option.value));
                }}
              />
            ) : (
              <p className="text-sm text-red-500 py-4 text-center">{t('User.EditPermissionsEmpty')}</p>
            )}
          </div>
          <div className="flex gap-2 justify-center mt-4 w-full">
            <TrackedButton
              variant="outline"
              buttonName="EDIT_PERMISSIONS_CANCELED"
              onClick={handleClose}
              className="w-full bg-white"
              isSubmit={false}>
              {t('Global.Canceled')}
            </TrackedButton>
            <TrackedButton onClick={onSubmit} isSubmit={false} buttonName="USER_PERMISSIONS_EDITED" className="w-full">
              <div className="text-white"> {t('User.Edit')} </div>
            </TrackedButton>
          </div>
        </div>
      </Modal>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center">
          <span className="text-[18px]"> {t('User.EditSuccess')} </span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={updatePermissions.isPending} onClose={() => {}}>
        <div className="text-center -mt-[9px]">{t('User.LoadingEdit')}</div>
      </ModalLoading>
    </>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { IncrementEvent } = tableServerRevision;

  return {
    IncrementEvent: (event: string) => dispatch(IncrementEvent(event)),
  };
};

export default connect(null, mapDispatchToProps)(UserEditPermissionsModal);
